<template>
  <b-modal
    :visible="visible"
    title="Visualizar PMAU"
    size="xl"
    dialog-class="pmau-viewer-dialog"
    content-class="pmau-viewer-content"
    body-class="pmau-viewer-body"
    hide-footer
    @change="(v) => $emit('change', v)"
    @show="Load"
    @hidden="Reset"
  >
    <div v-if="loading" class="pmau-viewer-state">
      <RotateSquare size="60px" class="animated fadeIn"></RotateSquare>
    </div>

    <div v-else-if="erro" class="pmau-viewer-state">
      <i class="fa fa-exclamation-circle fa-3x mb-3 text-warning"></i>
      <p class="mb-3">{{ erro }}</p>
      <button class="btn btn-sm btn-secondary" @click="Load">
        <i class="fa fa-redo mr-1"></i> Tentar novamente
      </button>
    </div>

    <div v-else-if="!doc" class="pmau-viewer-state">
      <i class="fa fa-book-open fa-3x mb-3 text-muted"></i>
      <p class="mb-0 text-muted">Esta versão do PMAU não está mais disponível.</p>
    </div>

    <template v-else>
      <div class="pmau-viewer-toolbar">
        <div class="pmau-viewer-info">
          <b-form-select
            :value="doc.id"
            :options="versionOptions"
            size="sm"
            class="pmau-viewer-version-select"
            @change="SelectVersion"
          ></b-form-select>
          <span v-if="doc.is_primary" class="badge pmau-viewer-primary-badge">
            <i class="fa fa-star mr-1"></i>Principal
          </span>
          <button
            v-else
            class="btn btn-sm pmau-viewer-btn-primary"
            :disabled="settingPrimary"
            @click="SetPrimary"
          >
            <i class="fa fa-star mr-1"></i> Tornar principal
          </button>
          <div class="pmau-viewer-meta small text-muted text-truncate" :title="doc.original_filename || ''">
            {{ doc.source === "uploaded" ? "Plano enviado" : "Gerado pelo sistema" }}
            · {{ formatDocDate(doc.created_at) }}
            <template v-if="doc.source === 'uploaded' && doc.original_filename">
              · {{ doc.original_filename }}
            </template>
          </div>
        </div>
        <div class="pmau-viewer-actions">
          <a
            v-if="pdfUrl"
            :href="pdfUrl"
            target="_blank"
            rel="noopener"
            class="btn btn-sm btn-secondary"
          >
            <i class="fa fa-external-link-alt mr-1"></i> Nova aba
          </a>
          <button
            v-if="doc.has_docx"
            class="btn btn-sm btn-word"
            :disabled="downloading"
            @click="Download('docx')"
          >
            <i class="fa fa-file-word mr-1"></i> .docx
          </button>
          <button
            class="btn btn-sm btn-pdf-adobe"
            :disabled="downloading"
            @click="Download('pdf')"
          >
            <i class="fa fa-file-pdf mr-1"></i> .pdf
          </button>
        </div>
      </div>

      <div v-if="loadingUrl" class="pmau-viewer-state">
        <RotateSquare size="48px" class="animated fadeIn"></RotateSquare>
      </div>
      <iframe
        v-else-if="pdfUrl"
        :key="pdfUrl"
        :src="pdfUrl"
        class="pmau-viewer-frame"
        title="PMAU"
      ></iframe>
    </template>
  </b-modal>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";

export default {
  name: "PmauViewer",
  components: { RotateSquare },
  model: { prop: "visible", event: "change" },
  props: {
    visible: { type: Boolean, default: false },
    contaId: { type: String, required: true },
    // Versão a abrir — botão "Visualizar" de uma versão em "Acessar PMAU".
    generationId: { type: String, default: null }
  },
  data() {
    return {
      loading: false,
      loadingUrl: false,
      erro: null,
      versions: [],
      doc: null,
      pdfUrl: null,
      downloading: false,
      settingPrimary: false
    };
  },
  computed: {
    versionOptions() {
      return this.versions.map(v => ({
        value: v.id,
        text:
          "v" + v.version_number +
          (v.is_primary ? " ★ Principal" : "") +
          " · " + (v.source === "uploaded" ? "Enviado" : "Gerado") +
          " · " + this.formatDocDate(v.created_at)
      }));
    }
  },
  methods: {
    async Load() {
      this.loading = true;
      this.erro = null;
      this.doc = null;
      this.pdfUrl = null;
      try {
        // Lista vem ordenada da versão mais recente para a mais antiga.
        var res = await PmauService.GetPmauDocumentGenerations(this.contaId);
        this.versions = res.data.filter(g => g.status === "completed" && g.has_pdf);
        if (this.versions.some(v => v.id === this.generationId)) {
          await this.SelectVersion(this.generationId);
        }
      } catch (e) {
        this.erro = "Erro ao carregar o PMAU.";
      } finally {
        this.loading = false;
      }
    },

    async SelectVersion(id) {
      var version = this.versions.find(v => v.id === id);
      if (!version) return;
      this.doc = version;
      this.pdfUrl = null;
      this.loadingUrl = true;
      try {
        // URL presignada expira: busca a cada troca de versão.
        var res = await PmauService.GetPmauDocumentDownloadUrl(this.contaId, id, "pdf", "inline");
        if (this.doc && this.doc.id === id) this.pdfUrl = res.data.download_url;
      } catch (e) {
        this.$notify({ data: ["Erro ao carregar o PDF desta versão."], type: "warn", duration: 5000 });
      } finally {
        this.loadingUrl = false;
      }
    },

    async SetPrimary() {
      if (this.settingPrimary || !this.doc) return;
      this.settingPrimary = true;
      try {
        await PmauService.SetPrimaryPmauDocument(this.contaId, this.doc.id);
        var id = this.doc.id;
        this.versions.forEach(v => { v.is_primary = v.id === id; });
        this.$emit("primary-changed");
      } catch (erro) {
        this.$notify({
          data: [
            erro.response && erro.response.data && erro.response.data.detail
              ? erro.response.data.detail
              : "Erro ao definir a versão principal."
          ],
          type: "warn",
          duration: 5000
        });
      } finally {
        this.settingPrimary = false;
      }
    },

    Reset() {
      this.versions = [];
      this.doc = null;
      this.pdfUrl = null;
      this.erro = null;
    },

    async Download(format) {
      if (this.downloading || !this.doc) return;
      this.downloading = true;
      try {
        var res = await PmauService.GetPmauDocumentDownloadUrl(this.contaId, this.doc.id, format);
        window.open(res.data.download_url, "_blank");
      } catch (e) {
        this.$notify({
          data: ["Erro ao gerar o link de download."],
          type: "warn",
          duration: 5000
        });
      } finally {
        this.downloading = false;
      }
    },

    formatDocDate(iso) {
      if (!iso) return "";
      var d = new Date(iso);
      return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    }
  }
};
</script>

<style scoped>
.pmau-viewer-state {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem;
}
.pmau-viewer-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #dee2e6;
}
.pmau-viewer-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1 1 auto;
}
.pmau-viewer-version-select {
  width: auto;
  max-width: 100%;
}
.pmau-viewer-meta {
  flex-basis: 100%;
}
.pmau-viewer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.pmau-viewer-primary-badge {
  background-color: #f0a500;
  color: #fff;
  font-size: 0.8rem;
  padding: 0.35em 0.6em;
}
.pmau-viewer-btn-primary {
  background-color: #f0a500;
  border-color: #f0a500;
  color: #fff;
}
.pmau-viewer-btn-primary:hover:not(:disabled) {
  background-color: #cc8c00;
  border-color: #cc8c00;
  color: #fff;
}
.pmau-viewer-frame {
  flex: 1 1 auto;
  width: 100%;
  border: 0;
  min-height: 0;
}
</style>

<style>
/* Classes aplicadas pelo b-modal (portado para fora do componente) — não podem ser scoped. */
.pmau-viewer-dialog {
  max-width: 95vw;
}
.pmau-viewer-content {
  height: 90vh;
}
.pmau-viewer-body {
  display: flex;
  flex-direction: column;
  padding: 0;
  min-height: 0;
  overflow: hidden;
}
</style>
